/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View, Text, Icon } from '@components/atoms'
import { mono, boShared } from '@components/systems/backoffice/backoffice.tokens'
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Chip,
	IconButton,
	CircularProgress,
} from '@mui/material'
import { filesize } from 'filesize'

import { inquiryMgmtApi } from '@api'
import { InquiryType, UserType } from '@schemas'
import {
	INQUIRY_REPLY_FROM_ADDRESS,
	INQUIRY_REPLY_FROM_NAME,
	INQUIRY_SUBJECT_PATTERN,
	customerFacingStaffName,
} from '@configs'
import { contactTopicLabel } from '@src/configs/contact.config'
import { InquiryReplyEditor } from './inquiryReplyEditor'

/** 서버와 같은 한도 — utils/inquiry/InquiryReply.ts */
const MAX_ATTACHMENTS = 5
const MAX_ATTACHMENT_EACH = 8 * 1024 * 1024
const MAX_ATTACHMENT_TOTAL = 7 * 1024 * 1024

interface PropsType {
	open: boolean
	inquiry: InquiryType | null
	/** 작성자 = 로그인한 스태프. 발신 주소는 CS 한 주소(INQUIRY_REPLY_FROM_ADDRESS) */
	sender: UserType
	onClose: () => void
	/** 발송 성공 — 서버가 바꾼 상태(신규 → 처리중)를 돌려준다 */
	onSent: (inquiryId: string, nextStatus: string) => void
}

const escapeHtml = (s: string) =>
	s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * 문의 이메일 답변 다이얼로그.
 * 받는 사람·보내는 사람은 고정, 제목과 본문(리치 텍스트)·첨부를 쓴다.
 * 원문은 서버 템플릿이 메일 아래에 인용한다.
 */
export const InquiryReplyDialog = (props: PropsType) => {
	const { open, inquiry, sender, onClose, onSent } = props

	const [subject, setSubject] = useState('')
	const [html, setHtml] = useState('')
	const [isBodyEmpty, setIsBodyEmpty] = useState(true)
	const [attachments, setAttachments] = useState<File[]>([])
	const [isSending, setIsSending] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')
	const fileInputRef = useRef<HTMLInputElement | null>(null)

	const initialHtml = useMemo(
		() =>
			// 서명은 고객이 읽는다 — 이름이 한글이면 Administrator
			`<p></p><p></p><p>Best regards,<br>${escapeHtml(
				customerFacingStaffName(sender.name),
			)}<br>Judahs</p>`,
		[sender.name],
	)

	// 문의가 바뀌거나 다시 열릴 때 초기화
	useEffect(() => {
		if (!open || !inquiry) return
		// 제목은 고객이 읽는다 — 영어 라벨(Contact 토픽)로만 만든다. 구분자도 ASCII '-' (em dash 는 서버가 거절)
		setSubject(`Re: Your inquiry to Judahs - ${contactTopicLabel(inquiry.categ)}`)
		setHtml(initialHtml)
		setIsBodyEmpty(false)
		setAttachments([])
		setErrorMessage('')
		setIsSending(false)
	}, [open, inquiry?.id, initialHtml])

	const totalBytes = attachments.reduce((sum, aFile) => sum + aFile.size, 0)

	const onPickFiles = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const picked = Array.from(e.target.files ?? [])
		e.target.value = ''
		setErrorMessage('')
		setAttachments(prev => {
			const next = [...prev]
			for (const aFile of picked) {
				if (next.length >= MAX_ATTACHMENTS) {
					setErrorMessage(`첨부는 ${MAX_ATTACHMENTS}개까지입니다.`)
					break
				}
				if (aFile.size > MAX_ATTACHMENT_EACH) {
					setErrorMessage(`${aFile.name}: 파일 하나는 8MB 까지입니다.`)
					continue
				}
				if (next.reduce((s, f) => s + f.size, 0) + aFile.size > MAX_ATTACHMENT_TOTAL) {
					setErrorMessage('첨부 합계는 7MB 까지입니다.')
					break
				}
				if (next.some(f => f.name === aFile.name && f.size === aFile.size)) continue
				next.push(aFile)
			}
			return next
		})
	}, [])

	const onRemoveFile = useCallback((index: number) => {
		setAttachments(prev => prev.filter((_, i) => i !== index))
	}, [])

	const onUploadImage = useCallback(
		async (file: File): Promise<string> => {
			if (!inquiry) return ''
			const res = await inquiryMgmtApi.uploadInquiryReplyImage({
				params: { inquiryId: inquiry.id },
				file,
			})
			if (res && (res.status === 200 || res.status === 201)) {
				const row = (res.data as any[])[0]
				return (row?.url as string) ?? ''
			}
			setErrorMessage('이미지 업로드에 실패했습니다.')
			return ''
		},
		[inquiry],
	)

	const onEditorChange = useCallback((nextHtml: string, empty: boolean) => {
		setHtml(nextHtml)
		setIsBodyEmpty(empty)
	}, [])

	// 제목에 한글 등 영어 밖 문자가 있으면 보내지 않는다(서버도 400)
	const isSubjectEnglish =
		subject.trim() === '' || INQUIRY_SUBJECT_PATTERN.test(subject.trim())

	const canSend =
		!!inquiry &&
		!isSending &&
		subject.trim() !== '' &&
		isSubjectEnglish &&
		!isBodyEmpty &&
		html.trim() !== ''

	const onSend = useCallback(async () => {
		if (!inquiry || !canSend) return
		setIsSending(true)
		setErrorMessage('')
		const res = await inquiryMgmtApi.sendInquiryReply({
			params: { inquiryId: inquiry.id },
			body: { subject: subject.trim(), html },
			files: attachments,
		})
		if (res && (res.status === 200 || res.status === 201)) {
			const row = (res.data as any[])[0]
			onSent(inquiry.id, (row?.status as string) ?? inquiry.status)
			return
		}
		setIsSending(false)
		const serverMessage =
			(res?.data as any)?.message ?? (res?.data as any[])?.[0]?.message
		setErrorMessage(
			typeof serverMessage === 'string'
				? `발송에 실패했습니다. (${serverMessage})`
				: '발송에 실패했습니다. 잠시 후 다시 시도하세요.',
		)
	}, [inquiry, canSend, subject, html, attachments, onSent])

	return (
		<Dialog
			open={open}
			onClose={isSending ? undefined : onClose}
			maxWidth="md"
			fullWidth
			css={style}
			PaperProps={{ className: 'replyDialogPaper' }}
		>
			<DialogTitle className="replyTitle">
				<Icon
					name="email-outline"
					style={{ fontSize: 20, marginRight: 8, color: mono.ink }}
				/>
				이메일 답변
				<IconButton
					size="small"
					onClick={onClose}
					disabled={isSending}
					sx={{ marginLeft: 'auto' }}
					title="닫기"
				>
					<Icon name="close" style={{ fontSize: 20, color: 'inherit' }} />
				</IconButton>
			</DialogTitle>

			<DialogContent className="replyContent">
				{inquiry && (
					<>
						<View className="fieldRow">
							<Text category="label" className="fieldLabel">
								받는 사람
							</Text>
							<Text category="p2">{`${inquiry.name || '-'} <${inquiry.email}>`}</Text>
						</View>
						<View className="fieldRow">
							<Text category="label" className="fieldLabel">
								보내는 사람
							</Text>
							<Text category="p2">{`${INQUIRY_REPLY_FROM_NAME} <${INQUIRY_REPLY_FROM_ADDRESS}>`}</Text>
							<Text category="c1" hint="500">
								{`· 작성자 ${
									sender.name || '-'
								} — 고객 회신은 ${INQUIRY_REPLY_FROM_ADDRESS} 로 옵니다`}
							</Text>
						</View>
						<View className="fieldRow">
							<Text category="label" className="fieldLabel">
								제목
							</Text>
							<TextField
								value={subject}
								onChange={e => setSubject(e.target.value)}
								size="small"
								fullWidth
								disabled={isSending}
								inputProps={{ maxLength: 300 }}
								error={!isSubjectEnglish}
								helperText={
									isSubjectEnglish ? undefined : '제목은 영어로만 쓸 수 있습니다.'
								}
							/>
						</View>

						<View className="editorWrap">
							<InquiryReplyEditor
								key={`${inquiry.id}-${open ? 'open' : 'closed'}`}
								initialHtml={initialHtml}
								placeholder="답변 내용을 입력하세요…"
								onUploadImage={onUploadImage}
								onChange={onEditorChange}
								disabled={isSending}
							/>
						</View>

						<View className="attachRow">
							<Button
								size="small"
								variant="outlined"
								startIcon={
									<Icon name="paperclip" style={{ fontSize: 16, color: 'inherit' }} />
								}
								onClick={() => fileInputRef.current?.click()}
								disabled={isSending || attachments.length >= MAX_ATTACHMENTS}
							>
								파일 첨부
							</Button>
							<input
								ref={fileInputRef}
								type="file"
								multiple
								className="hiddenInput"
								onChange={onPickFiles}
							/>
							{attachments.map((aFile, index) => (
								<Chip
									key={`${aFile.name}-${aFile.size}`}
									label={`${aFile.name} · ${filesize(aFile.size)}`}
									size="small"
									onDelete={isSending ? undefined : () => onRemoveFile(index)}
									className="attachChip"
								/>
							))}
							{attachments.length > 0 && (
								<Text category="c1" hint="500" className="attachTotal">
									{`${attachments.length}/${MAX_ATTACHMENTS} · ${filesize(
										totalBytes,
									)} / 7 MB`}
								</Text>
							)}
						</View>

						<Text category="c1" hint="500" className="quoteNote">
							고객이 보낸 원문이 메일 아래에 인용되어 함께 발송됩니다. 보내면 타임라인에
							남고, 상태가 신규면 처리중으로 바뀝니다.
						</Text>

						{errorMessage && (
							<Text category="c1" className="errorText">
								{errorMessage}
							</Text>
						)}
					</>
				)}
			</DialogContent>

			<DialogActions className="replyActions">
				<Button onClick={onClose} disabled={isSending}>
					취소
				</Button>
				<Button
					variant="contained"
					onClick={() => {
						void onSend()
					}}
					disabled={!canSend}
					startIcon={
						isSending ? (
							<CircularProgress size={14} color="inherit" />
						) : (
							<Icon name="send" style={{ fontSize: 16, color: 'inherit' }} />
						)
					}
				>
					{isSending ? '보내는 중…' : '보내기'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

const style = css({
	...boShared,

	'.replyDialogPaper': {
		height: 'min(88vh, 60rem)',
		display: 'flex',
		flexDirection: 'column',
	},
	'.replyTitle': {
		display: 'flex',
		alignItems: 'center',
		fontSize: 16,
		fontWeight: 500,
		padding: '14px 12px 14px 24px',
		borderBottom: `1px solid ${mono.line}`,
	},
	'.replyContent': {
		display: 'flex',
		flexDirection: 'column',
		gap: 10,
		padding: '16px 24px',
		minHeight: 0,
	},
	'.fieldRow': {
		display: 'flex',
		alignItems: 'center',
		gap: 12,
		textAlign: 'left',
		'.text': { color: mono.ink, fontWeight: 300 },
	},
	'.fieldLabel': {
		minWidth: '5.5rem',
		flexShrink: 0,
		fontSize: '11px !important',
		fontWeight: '500 !important',
		letterSpacing: '.16em',
		textTransform: 'uppercase',
		color: `${mono.faint} !important`,
	},
	'.editorWrap': {
		flex: 1,
		minHeight: '18rem',
		display: 'flex',
		flexDirection: 'column',
		'& > div': { flex: 1 },
	},
	'.attachRow': {
		display: 'flex',
		alignItems: 'center',
		flexWrap: 'wrap',
		gap: 8,
	},
	'.attachChip': { maxWidth: '20rem' },
	'.attachTotal': { marginLeft: 'auto', color: mono.faint },
	'.hiddenInput': { display: 'none' },
	'.quoteNote': { textAlign: 'left', color: mono.faint, fontWeight: 300 },
	'.errorText': { color: mono.error, textAlign: 'left' },
	'.replyActions': {
		padding: '12px 24px 16px',
		borderTop: `1px solid ${mono.line}`,
		gap: 8,
	},
})
